import { Form, Row, Col, InputGroup, FormSelect } from 'react-bootstrap';
import { useContext, useEffect, useMemo, useState } from 'react';
import { error as errorAlert, infoAlert, successAlert, warningAlert } from '../../components/toastr';
import { SubmitButton, CancelButton } from '../../components/btns';
import { DateSelector, TimeSelector } from '../../components/datetimepicker';
import { UCWords } from '../../components/resources';
import { EventCategories, EventColours } from '../../assets/constants';
import { PageLoading } from '../../components/loading';
import { getClientContacts, getClients } from '../../resources/api/clients';
import SelectInput from '../../components/select';
import { Link, useNavigate } from 'react-router-dom';
import { createJob, updateJob } from '../../resources/api/jobs';
import { getUsers } from '../../resources/api/users';
import { AppUserContext } from "../../App";
import { getProjects } from '../../resources/api/projects';

const { Control, Control: { Feedback }, Label } = Form;

/**
 * handle page for creating trip
 * @param {Object} props
 * @param {"edit"|"create"} props.action
 * @param {import('../../resources/api/jobs').JobObject} props.details
 * @param {(details: import('../../resources/api/jobs').JobObject) => void} props.onSubmit
 */
const JobForm = ({ details: JobObject, action, onSubmit }) => {

    const { profile } = useContext(AppUserContext);

    const [details, setDetails] = useState(JobObject);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);

    const [client_contacts, setClientContacts] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [validated, setValidated] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);

    const [users, setUsers] = useState([]);



    const navigate = useNavigate();



    /**
     * handle the overall submitting of the form
     * @param {React.FormEvent} e
     */
    const handleSubmit = e => {
        const form = e.currentTarget;

        e.preventDefault();

        if (!form.checkValidity()) {
            setValidated(true);
            errorAlert("You have errors in your form. These have been highlighted for you.", "Form Errors");
            return;
        }


        setValidated(false);
        setSubmitting(true);

        let promise;

        const data = {
            ...details
        };

        // console.log(data);
        // setSubmitting(false);
        // return;

        if (action === "create") {
            promise = createJob(data);
        } else {
            promise = updateJob(data, details.id);
        }

        promise
            .then(({ job, message }) => {
                successAlert(message);
                onSubmit(job);
            })
            .catch(e => {
                errorAlert(e);
                setSubmitting(false);
            })
    }

    useEffect(() => {
        Promise.all([
            getClients(),
            getUsers()
        ])

            .then(([{ clients }, { users }]) => {
                setClients(
                    clients.map(c => ({
                        ...c,
                        description: c.address
                    }))
                );
                setUsers(
                    users.map(c => ({
                        ...c,
                        id: c.contact_id,
                        title: c.display_name,
                        description: `E: ${c.email} | T: ${c.telephone} | ${c.job_title}`
                    }))
                );
            })
            .catch(e => errorAlert(e))
            .finally(() => setLoaded(true));
    }, []);

    useEffect(() => {

        if (!details.client_id) return;

        infoAlert("Getting client's contact persons...");

        getClientContacts(details.client_id)
            .then(({ contact_persons }) => {
                setClientContacts(
                    contact_persons
                        .map(c => ({
                            ...c,
                            title: c.display_name,
                            id: c.contact_id,
                            description: `${c.telephone} | ${c.email}`
                        })));

                if (contact_persons.length === 0) {
                    warningAlert(
                        "This client has no contact persons. Please click to add contact person...",
                        "",
                        {
                            onclick: () => navigate(`/app/clients/${details.client_id}`)
                        }
                    )
                } else {
                    successAlert("Loaded client's contact persons");
                }
            })
            .catch(errorAlert)

        getProjects(false, { client_id: details.client_id })
            .then(({ projects }) => {
                setProjects(projects.map(p => ({ ...p, description: `${p.code} - ${p.title}` })))
            })
            .catch(errorAlert)

    }, [details.client_id, navigate])




    if (!loaded) return <PageLoading>Loading clients...</PageLoading>;

    return (
        <Form className="max-800" validated={validated} noValidate onSubmit={handleSubmit}>
            <h4 className="form-section-label">Details</h4>
            <Row className="g-2">
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Job Type</Label>
                    <div className='mt-1'>
                        <Form.Check
                            type="radio"
                            inline
                            id={`walk_in`}
                            name="job_type"
                            label={`Walk In`}
                            checked={details.type === "walk_in"}
                            onChange={() => setDetails(d => ({ ...d, type: "walk_in" }))}
                            required
                        />
                        <Form.Check
                            type="radio"
                            inline
                            id={`project`}
                            name="job_type"
                            label={`Project`}
                            checked={details.type === "project"}
                            onChange={() => setDetails(d => ({ ...d, type: "project" }))}
                            required
                        />
                    </div>
                </Col>
            </Row>
            <Row className='g-2'>
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Client</Label>
                    <SelectInput
                        value={[details.client_id]}
                        onChange={([id]) => setDetails(d => ({ ...d, client_id: id || "", client_contact_id: "", project_id: "" }))}
                        options={clients}
                        required
                        placeholder='-'
                        maxItems={1}
                        errorText="This field is required"
                    />
                    <Form.Text muted style={{ lineHeight: 1, display: "block" }}>
                        The client must exist in the existing lists of clients.
                        <Link to="/app/clients/new?action=create_job">Click here</Link> to add a new client.
                    </Form.Text>
                </Col>
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Client Contact</Label>
                    <SelectInput
                        value={[details.client_contact_id]}
                        onChange={([id]) => setDetails(d => ({ ...d, client_contact_id: id || "" }))}
                        options={client_contacts}
                        required
                        placeholder="-"
                        maxItems={1}
                        errorText="This field is required."
                    />
                    {!details.client_id &&
                        <Form.Text muted style={{ lineHeight: 1, display: "block" }}>
                            Select a client first.
                        </Form.Text>
                    }

                </Col>
            </Row>
            {(details.type === "project") &&
                <Row className='g-2'>
                    <Col sm={6} className="my-1">
                        <Label className="form-field-title">Project</Label>
                        <SelectInput
                            value={[details.project_id]}
                            onChange={([id]) => setDetails(d => ({ ...d, project_id: id || "", }))}
                            options={projects}
                            required
                            placeholder='-'
                            maxItems={1}
                            errorText="This field is required"
                        />
                        <Form.Text muted style={{ lineHeight: 1, display: "block" }}>
                            The client must exist in the existing lists of clients.
                            <Link to="/app/clients/new?action=create_job">Click here</Link> to add a new client.
                        </Form.Text>
                    </Col>
                </Row>

            }
            {/* <Row className='g-2'>
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Title</Label>
                    <Control
                        value={details.title}
                        onChange={e => setDetails({ ...details, title: e.currentTarget.value })}
                        placeholder=""
                        required={true}
                    />
                    <Feedback type="invalid">
                        A title must be provided for this job
                    </Feedback>
                </Col>
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Category</Label>
                    <Form.Select
                        value={details.category}
                        onChange={e => setDetails({ ...details, category: e.currentTarget.value })}
                        required
                    >
                        <option value=""></option>
                        {EventCategories.map(t => <option value={t} key={t}>{UCWords(t.replace(/-/g, " "))}</option>)}
                    </Form.Select>
                    <Feedback type="invalid">
                        Please choose a category for this event.
                    </Feedback>
                </Col>
            </Row> */}
            <Row className="g-2">
                <Col xs={6} sm={4} className="my-1">
                    <Label className="form-field-title">Start Date</Label>
                    <DateSelector
                        value={details.job_date}
                        onChange={date =>
                            setDetails(d => ({
                                ...d,
                                job_date: date,
                                end_date: (!d.end_date || (d.end_date < date)) ? date : d.end_date
                            }))}
                        // placeholder="when is the event taking place?"
                        required={true}
                        minDate={(profile.permission_level === 1) ? false : "2021-01-01"}

                    />
                    <Feedback type="invalid">
                        A valid date must be provided.
                    </Feedback>
                </Col>
                <Col xs={6} sm={4} className="my-1">
                    <Label className="form-field-title">End Date</Label>
                    <DateSelector
                        value={details.end_date}
                        onChange={date =>
                            setDetails(d => ({
                                ...d,
                                end_date: date
                            }))}
                        // placeholder="when is the event taking place?"
                        required={true}
                        minDate={details.job_date}

                    />
                    <Feedback type="invalid">
                        A valid date must be provided.
                    </Feedback>
                </Col>
                {/* <Col xs={6} sm={2} className="my-1">
                    <Label className="form-field-title">End Time</Label>
                    <TimeSelector
                        value={details.end_time}
                        onChange={date =>
                            setDetails(d => ({
                                ...d,
                                end_time: date
                            }))}
                        // placeholder="when is the event taking place?"
                        required={true}
                        minDate={details.job_date}

                    />
                    <Feedback type="invalid">
                        A valid date must be provided.
                    </Feedback>
                </Col> */}
            </Row>
            <Row className="g-2">
                {/* <Col sm={6} className="my-1">
                    <Label className="form-field-title">Location</Label>
                    <Control
                        value={details.location}
                        onChange={e => setDetails({ ...details, location: e.currentTarget.value })}
                        placeholder=""
                        required={true}
                    />
                    <Feedback type="invalid">
                        A location must be provided
                    </Feedback>
                    <Form.Text muted>
                        Format of [building name], [streetname], [city]
                    </Form.Text>
                </Col> */}
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Lead Contact</Label>
                    <SelectInput
                        value={[details.lead_contact_id]}
                        onChange={([id]) => setDetails(d => ({ ...d, lead_contact_id: id || "" }))}
                        placeholder="-"
                        options={users}
                        maxItems={1}
                        required={true}
                    />
                </Col>
            </Row>


            <Row>
                <Col className="mt-4 mb-3 text-end">
                    <SubmitButton isSubmitting={isSubmitting} type="submit">
                        {action === "edit" ? "Update Job" : "Create Job"}
                    </SubmitButton>
                    <CancelButton isSubmitting={isSubmitting}>
                        Cancel
                    </CancelButton>
                </Col>
            </Row>
        </Form>
    )
}


export default JobForm;